import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { PHOTO_COLUMNS, albumPhotos, photoUrl, sortPhotos } from '../lib/gallery';
import { MAX_FILES_PER_UPLOAD, UploadError, removeFiles, uploadPhoto } from '../lib/galleryUpload';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { CATEGORY_MAX, DESCRIPTION_MAX, TITLE_MAX, canManageGallery } from './AdminGallery';
import './AdminDashboard.css';
import './AdminGallery.css';

const ALT_MAX = 200;
const CAPTION_MAX = 300;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const describeError = (err, fallback) => {
  if (err?.code === '42501') return 'You do not have permission to change the gallery';
  return fallback;
};

const AdminGalleryAlbum = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = canManageGallery(user);
  const navigate = useNavigate();
  const location = useLocation();

  const [album, setAlbum] = useState(null);
  const [details, setDetails] = useState({ title: '', category: '', description: '', sort_order: 0 });
  const [photos, setPhotos] = useState([]);
  const [drafts, setDrafts] = useState({}); // photo id -> { alt_text, caption } being edited
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState({ type: location.state?.message ? 'success' : '', text: location.state?.message ?? '' });
  const [busy, setBusy] = useState('');
  const [progress, setProgress] = useState('');
  const [uploadErrors, setUploadErrors] = useState([]);

  const load = useCallback(async () => {
    if (!UUID.test(id)) {
      setLoadError('This album does not exist.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    const { data, error } = await supabase
      .from('gallery_albums')
      .select(`id, title, description, category, sort_order, is_published, published_at, cover_image_id, ${albumPhotos(PHOTO_COLUMNS)}`)
      .eq('id', id)
      .maybeSingle();
    if (error) {
      console.error('Failed to load album', error);
      setLoadError('Failed to load the album');
    } else if (!data) {
      setLoadError('This album does not exist.');
    } else {
      setAlbum(data);
      setDetails({ title: data.title, category: data.category, description: data.description, sort_order: data.sort_order });
      setPhotos(sortPhotos(data.gallery_images));
      setDrafts({});
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const say = (type, text) => setMessage({ type, text });

  const saveDetails = async (e) => {
    e.preventDefault();
    const title = details.title.trim();
    if (!title) {
      say('error', 'Enter a title for the album');
      return;
    }
    setBusy('details');
    const { data, error } = await supabase
      .from('gallery_albums')
      .update({
        title, category: details.category.trim(), description: details.description.trim(),
        sort_order: Number.parseInt(details.sort_order, 10) || 0
      })
      .eq('id', id)
      .select('id');
    setBusy('');
    if (error || !data?.length) {
      console.error('Failed to save album', error);
      say('error', describeError(error ?? { code: '42501' }, 'The album could not be saved'));
      return;
    }
    say('success', 'Album details saved');
    await load();
  };

  const setPublished = async (publish) => {
    setBusy('publish');
    const { data, error } = await supabase.from('gallery_albums').update({ is_published: publish }).eq('id', id).select('id');
    setBusy('');
    if (error || !data?.length) {
      console.error('Failed to change publication', error);
      say('error', error?.code === '23514'
        ? 'Add at least one photo before publishing'
        : describeError(error ?? { code: '42501' }, publish ? 'The album could not be published' : 'The album could not be unpublished'));
      return;
    }
    say('success', publish ? 'Published: the album is now on the Gallery page' : 'Unpublished: the album is hidden from visitors');
    await load();
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;
    setUploadErrors([]);
    if (files.length > MAX_FILES_PER_UPLOAD) {
      say('error', `Choose at most ${MAX_FILES_PER_UPLOAD} photos at a time`);
      return;
    }
    setBusy('upload');
    say('', '');
    const errors = [];
    let added = 0;
    let next = photos.reduce((max, p) => Math.max(max, p.sort_order), 0) + 1;
    for (const [i, file] of files.entries()) {
      setProgress(`Uploading ${i + 1} of ${files.length}...`);
      try {
        // Default description: the album title; staff can make it more specific afterwards.
        await uploadPhoto(id, file, { altText: album.title, sortOrder: next });
        next += 1;
        added += 1;
      } catch (err) {
        if (!(err instanceof UploadError)) console.error('Unexpected upload error', err);
        errors.push(err instanceof UploadError ? err.message : `${file.name} could not be uploaded`);
      }
    }
    setProgress('');
    setBusy('');
    setUploadErrors(errors);
    if (added) say('success', `${added} ${added === 1 ? 'photo' : 'photos'} added. Check each photo's description below.`);
    await load();
  };

  const savePhoto = async (photo) => {
    const draft = drafts[photo.id];
    const alt = draft.alt_text.trim();
    if (!alt) {
      say('error', 'Each photo needs a description for visitors who cannot see it');
      return;
    }
    setBusy(`photo-${photo.id}`);
    const { data, error } = await supabase
      .from('gallery_images')
      .update({ alt_text: alt, caption: draft.caption.trim() })
      .eq('id', photo.id)
      .select('id');
    setBusy('');
    if (error || !data?.length) {
      console.error('Failed to save photo', error);
      say('error', describeError(error ?? { code: '42501' }, 'The photo could not be saved'));
      return;
    }
    say('success', 'Photo saved');
    await load();
  };

  const makeCover = async (photo) => {
    setBusy(`photo-${photo.id}`);
    const { data, error } = await supabase.from('gallery_albums').update({ cover_image_id: photo.id }).eq('id', id).select('id');
    setBusy('');
    if (error || !data?.length) {
      say('error', describeError(error ?? { code: '42501' }, 'The cover could not be changed'));
      return;
    }
    say('success', 'Cover photo changed');
    await load();
  };

  // Moves a photo one place earlier (-1) or later (+1) and renumbers the album's photos 1, 2, 3, ...
  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= photos.length) return;
    const order = [...photos];
    [order[index], order[target]] = [order[target], order[index]];
    setBusy('order');
    const changes = order.map((p, i) => ({ p, sort: i + 1 })).filter(({ p, sort }) => p.sort_order !== sort);
    const results = await Promise.all(changes.map(({ p, sort }) =>
      supabase.from('gallery_images').update({ sort_order: sort }).eq('id', p.id).select('id')));
    setBusy('');
    const failed = results.find((r) => r.error || !r.data?.length);
    if (failed) say('error', describeError(failed.error ?? { code: '42501' }, 'The order could not be saved'));
    await load();
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm('Delete this photo? It is removed from the site straight away.')) return;
    setBusy(`photo-${photo.id}`);
    const { data, error } = await supabase.from('gallery_images').delete().eq('id', photo.id).select('id');
    if (error || !data?.length) {
      setBusy('');
      say('error', describeError(error ?? { code: '42501' }, 'The photo could not be deleted'));
      return;
    }
    await removeFiles([photo.storage_path]);
    setBusy('');
    const wasLast = photos.length === 1 && album.is_published;
    say('success', wasLast ? 'Photo deleted. The album had no photos left, so it was unpublished.' : 'Photo deleted');
    await load();
  };

  const deleteAlbum = async () => {
    if (!window.confirm(`Delete the album "${album.title}" and all ${photos.length} photos? This cannot be undone.`)) return;
    setBusy('delete');
    const paths = photos.map((p) => p.storage_path);
    const { data, error } = await supabase.from('gallery_albums').delete().eq('id', id).select('id');
    if (error || !data?.length) {
      setBusy('');
      say('error', describeError(error ?? { code: '42501' }, 'The album could not be deleted'));
      return;
    }
    await removeFiles(paths);
    navigate('/admin/gallery', { state: { message: `Album "${album.title}" deleted` } });
  };

  const editDraft = (photo, field, value) =>
    setDrafts((d) => ({ ...d, [photo.id]: { alt_text: photo.alt_text, caption: photo.caption, ...d[photo.id], [field]: value } }));

  if (loading && !album) {
    return (
      <AdminLayout>
        <div className="admin-dashboard admin-gallery">
          <div className="loading-state"><div className="spinner"></div><p>Loading album...</p></div>
        </div>
      </AdminLayout>
    );
  }

  if (loadError) {
    return (
      <AdminLayout>
        <div className="admin-dashboard admin-gallery">
          <h1>Album</h1>
          <div className="error-banner" role="alert">{loadError}</div>
          <Link to="/admin/gallery">Back to Gallery</Link>
        </div>
      </AdminLayout>
    );
  }

  const disabled = !canEdit || Boolean(busy);

  return (
    <AdminLayout>
      <div className="admin-dashboard admin-gallery">
        <p><Link to="/admin/gallery">← All albums</Link></p>
        <div className="dashboard-header">
          <div>
            <h1>{album.title}</h1>
            <p>
              <span className={`badge ${album.is_published ? 'badge-published' : 'badge-draft'}`}>
                {album.is_published ? 'Published' : 'Draft'}
              </span>{' '}
              {album.is_published && album.published_at && `since ${new Date(album.published_at).toLocaleDateString()} · `}
              {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
            </p>
          </div>
          {canEdit && (
            <div className="gallery-actions">
              {album.is_published ? (
                <button type="button" className="btn btn-secondary" disabled={disabled} onClick={() => setPublished(false)}>
                  Unpublish
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={disabled || photos.length === 0}
                  aria-describedby={photos.length === 0 ? 'publish-hint' : undefined}
                  onClick={() => setPublished(true)}
                >
                  Publish
                </button>
              )}
            </div>
          )}
        </div>
        {canEdit && !album.is_published && photos.length === 0 && (
          <p id="publish-hint" className="help-text">Add at least one photo before publishing.</p>
        )}
        {!canEdit && <p className="help-text">You can view this album. Only admins can change the gallery.</p>}

        {message.text && (
          <div className={message.type === 'error' ? 'error-banner' : 'alert-success'} role={message.type === 'error' ? 'alert' : 'status'}>
            {message.text}
          </div>
        )}

        <form className="gallery-section" onSubmit={saveDetails} noValidate>
          <h2>Album details</h2>
          <div className="gallery-fields">
            <label htmlFor="edit-title">Title</label>
            <input id="edit-title" value={details.title} maxLength={TITLE_MAX} disabled={!canEdit}
              onChange={(e) => setDetails({ ...details, title: e.target.value })} />
            <label htmlFor="edit-category">Category (optional)</label>
            <input id="edit-category" value={details.category} maxLength={CATEGORY_MAX} disabled={!canEdit}
              onChange={(e) => setDetails({ ...details, category: e.target.value })} />
            <label htmlFor="edit-description">Description (optional)</label>
            <textarea id="edit-description" rows={2} value={details.description} maxLength={DESCRIPTION_MAX} disabled={!canEdit}
              onChange={(e) => setDetails({ ...details, description: e.target.value })} />
            <label htmlFor="edit-order">Position on the Gallery page (lower numbers first)</label>
            <input id="edit-order" type="number" inputMode="numeric" value={details.sort_order} disabled={!canEdit}
              onChange={(e) => setDetails({ ...details, sort_order: e.target.value })} />
          </div>
          {canEdit && (
            <button type="submit" className="btn btn-primary" disabled={disabled}>
              {busy === 'details' ? 'Saving...' : 'Save details'}
            </button>
          )}
        </form>

        <section className="gallery-section" aria-labelledby="photos-heading">
          <h2 id="photos-heading">Photos</h2>
          {canEdit && (
            <div className="gallery-upload">
              <label htmlFor="photo-upload" className="btn btn-primary">Add photos</label>
              <input
                id="photo-upload"
                className="visually-hidden"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                disabled={disabled}
                onChange={upload}
              />
              <p className="help-text">
                JPEG, PNG or WebP, up to {MAX_FILES_PER_UPLOAD} at a time. Photos are resized and their hidden
                details (such as the location where they were taken) are removed before upload. Only upload photos
                the school has permission to publish.
              </p>
              {progress && <p role="status">{progress}</p>}
              {uploadErrors.length > 0 && (
                <div className="error-banner" role="alert">
                  Some photos were not added:
                  <ul className="gallery-upload-errors">
                    {uploadErrors.map((text) => <li key={text}>{text}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {photos.length === 0 ? (
            <p className="no-data">No photos in this album yet.</p>
          ) : (
            <ul className="gallery-photo-list">
              {photos.map((photo, index) => {
                const draft = drafts[photo.id] ?? { alt_text: photo.alt_text, caption: photo.caption };
                const dirty = draft.alt_text !== photo.alt_text || draft.caption !== photo.caption;
                const isCover = album.cover_image_id ? album.cover_image_id === photo.id : index === 0;
                const label = `photo ${index + 1}`;
                return (
                  <li key={photo.id} className="gallery-photo">
                    <img src={photoUrl(photo.storage_path)} alt={photo.alt_text} loading="lazy" />
                    <div className="gallery-photo-body">
                      {isCover && <span className="badge badge-published">Cover</span>}
                      <label htmlFor={`alt-${photo.id}`}>Description for visitors who cannot see the photo</label>
                      <input id={`alt-${photo.id}`} value={draft.alt_text} maxLength={ALT_MAX} disabled={!canEdit}
                        onChange={(e) => editDraft(photo, 'alt_text', e.target.value)} />
                      <label htmlFor={`caption-${photo.id}`}>Caption (optional, shown under the photo)</label>
                      <input id={`caption-${photo.id}`} value={draft.caption} maxLength={CAPTION_MAX} disabled={!canEdit}
                        onChange={(e) => editDraft(photo, 'caption', e.target.value)} />
                      {canEdit && (
                        <div className="gallery-actions">
                          {dirty && (
                            <button type="button" className="btn btn-primary" disabled={disabled} onClick={() => savePhoto(photo)}>
                              Save<span className="visually-hidden"> {label}</span>
                            </button>
                          )}
                          {!isCover && (
                            <button type="button" className="btn btn-secondary" disabled={disabled} onClick={() => makeCover(photo)}>
                              Make cover<span className="visually-hidden"> ({label})</span>
                            </button>
                          )}
                          <button type="button" className="btn btn-secondary" disabled={disabled || index === 0}
                            onClick={() => move(index, -1)} aria-label={`Move ${label} earlier`}>↑</button>
                          <button type="button" className="btn btn-secondary" disabled={disabled || index === photos.length - 1}
                            onClick={() => move(index, 1)} aria-label={`Move ${label} later`}>↓</button>
                          <button type="button" className="btn btn-danger" disabled={disabled} onClick={() => deletePhoto(photo)}>
                            Delete<span className="visually-hidden"> {label}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {canEdit && (
          <section className="gallery-section" aria-labelledby="delete-heading">
            <h2 id="delete-heading">Delete album</h2>
            <p className="help-text">Removes the album and all its photos from the site and from storage.</p>
            <button type="button" className="btn btn-danger" disabled={disabled} onClick={deleteAlbum}>
              Delete album
            </button>
          </section>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGalleryAlbum;
